import { Field, Modal, modalWidth, Button, FormikField, ModalStyle, Money } from "@hubrise/react-components"
import { Form, Formik } from "formik"
import type { FormikErrors } from "formik"
import _ from "lodash"
import * as React from "react"
import { useTranslation } from "react-i18next"

import useEditor from "shared/hooks/useEditor"
import { CategoryId } from "shared/hubrise/ids"
import { IVariant } from "shared/hubrise/models"

import { ItemType, RoundingOptions, BulkUpdatePricesRules } from "./types"

type BulkUpdatePricesFormValues = {
  sourceVariantRef: Array<string>
  rounding: Array<RoundingOptions>
  percentage: number
  skipAlreadySetPrices: boolean
  categoryIds: Array<string>
  optionListNames: Array<string>
}

interface BulkUpdatePricesProps {
  targetVariant: IVariant | null
  itemType: ItemType
  onSubmit: (rules: BulkUpdatePricesRules) => void
  close: () => void
}

const BulkUpdatePrices: React.FC<BulkUpdatePricesProps> = ({ targetVariant, itemType, onSubmit, close }) => {
  const { t } = useTranslation()
  const { catalog } = useEditor()
  const variants = catalog.data.variants
  const currency = catalog.currency

  const initialValues: BulkUpdatePricesFormValues = {
    sourceVariantRef: ["default"],
    percentage: 0,
    rounding: ["None"],
    skipAlreadySetPrices: false,
    categoryIds: [],
    optionListNames: [],
  }

  const handleSubmit = (values: BulkUpdatePricesFormValues) => {
    const rules: BulkUpdatePricesRules = {
      sourceVariantRef: values.sourceVariantRef[0] === "default" ? null : values.sourceVariantRef[0],
      rounding: values.rounding[0],
      percentage: values.percentage,
      skipAlreadySetPrices: values.skipAlreadySetPrices,
      categoryIds: values.categoryIds.length > 0 ? values.categoryIds.map((id) => CategoryId(Number(id))) : null,
      optionListNames: values.optionListNames.length > 0 ? values.optionListNames : null,
    }
    onSubmit(rules)
    close()
  }

  const validate = (values: BulkUpdatePricesFormValues): FormikErrors<BulkUpdatePricesFormValues> => {
    const errors: FormikErrors<BulkUpdatePricesFormValues> = {}
    if (values.percentage === null) errors.percentage = t("validations.not_empty")
    return errors
  }

  return (
    <Modal
      modalWidth={modalWidth.medium}
      onClose={close}
      renderContent={({ close }) => (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
          <Form>
            <ModalStyle.Header>
              <ModalStyle.Title>
                {targetVariant === null
                  ? t("update_all_prices_modal.title.default")
                  : t("update_all_prices_modal.title.variant", { variantName: targetVariant.name })}
              </ModalStyle.Title>
              <ModalStyle.Close onClick={close} />
            </ModalStyle.Header>

            <ModalStyle.Body>
              <FormikField.Select
                name="sourceVariantRef"
                label={t("update_all_prices_modal.source_variant.label")}
                withMargin
                options={[
                  { label: t("update_all_prices_modal.source_variant.default"), value: "default" },
                  ...variants.map((variant) => ({ label: variant.name, value: variant.ref })),
                ]}
                searchable={false}
              />

              <FormikField.NumberInput
                name="percentage"
                label={t("update_all_prices_modal.percentage_label")}
                withMargin
                max={100}
                min={-100}
                step={5}
                prefix="%"
              />

              <FormikField.Select
                name="rounding"
                label={t("update_all_prices_modal.rounding.label")}
                withMargin
                options={[
                  { label: t("update_all_prices_modal.rounding.no_rounding"), value: "None" },
                  {
                    label: t("update_all_prices_modal.rounding.to_closest", {
                      value: new Money(5, currency).toHumanString({ isHtml: false }),
                    }),
                    value: "0.05",
                  },
                  {
                    label: t("update_all_prices_modal.rounding.to_closest", {
                      value: new Money(10, currency).toHumanString({ isHtml: false }),
                    }),
                    value: "0.10",
                  },
                ]}
                searchable={false}
              />

              <FormikField.Checkbox name="skipAlreadySetPrices" text={t("update_all_prices_modal.skip_label")} />

              {itemType === "skus" && <SkuFields />}
              {itemType === "options" && <OptionFields />}
            </ModalStyle.Body>

            <ModalStyle.Footer>
              <Button variant="primary" type="submit">
                {t("general.update")}
              </Button>
              <Button variant="empty" onClick={close}>
                {t("general.cancel")}
              </Button>
            </ModalStyle.Footer>
          </Form>
        </Formik>
      )}
    />
  )
}

const SkuFields = (): JSX.Element => {
  const { t } = useTranslation()
  const { catalog } = useEditor()
  const [selectCategories, setSelectCategories] = React.useState(false)

  return (
    <>
      <Field.Checkbox
        name="selectCategories"
        value={selectCategories}
        text={t("update_all_prices_modal.select_categories.label")}
        onChange={(state) => setSelectCategories(state)}
      />
      {selectCategories && (
        <FormikField.Select
          name="categoryIds"
          placeholder={t("update_all_prices_modal.select_categories.categories")}
          options={catalog.data.categories.map((category) => ({
            label: category.name,
            value: String(category.id),
          }))}
          searchable={true}
          isMulti={true}
        />
      )}
    </>
  )
}

const OptionFields = (): JSX.Element => {
  const { t } = useTranslation()
  const { catalog } = useEditor()
  const [selectOptionLists, setSelectOptionLists] = React.useState(false)

  return (
    <>
      <Field.Checkbox
        name="selectOptionLists"
        value={selectOptionLists}
        text={t("update_all_prices_modal.select_option_lists.label")}
        onChange={(state) => setSelectOptionLists(state)}
      />
      {selectOptionLists && (
        <FormikField.Select
          name="optionListNames"
          label={t("update_all_prices_modal.select_option_lists.names")}
          options={_.uniq(catalog.data.optionLists.map((optionList) => optionList.name))
            .sort()
            .map((optionListName) => ({
              label: optionListName,
              value: optionListName,
            }))}
          searchable={true}
          isMulti={true}
        />
      )}
    </>
  )
}

export default BulkUpdatePrices
